import React, { useState, useEffect, useMemo, useDeferredValue, useCallback } from "react";
import {
  useNavigate,
  useSearchParams,
  Link as RouterLink,
} from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  useMediaQuery,
  useTheme,
  Alert,
  Autocomplete,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  format,
  getDaysInMonth,
  eachDayOfInterval,
  isSameDay,
  parseISO,
  startOfMonth,
  endOfMonth,
} from "date-fns";
import {
  getMonthlyAttendance,
  saveAttendanceRecord,
  saveAllAttendance,
  exportAttendanceReport,
  getAllAnimals,
} from "../../api/animalAttendance";
import { getActivities, getActivityById, createActivity } from "../../api/planning";
import AttendanceFilters from "../../components/animal/AttendanceFilters";
import AttendanceTable from "./AttendanceTable";
import AttendanceDayList from "./AttendanceDayList";
import AttendanceStats from "../../components/animal/AttendanceStats";
import AttendanceDialog from "../../components/animal/AttendanceDialog";
import StockTakingActivitySummary from "../../components/animal/StockTakingActivitySummary";
import useAuth from "../../hooks/useAuth";

export default function AnimalAttendance() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const theme = useTheme();
  const showMonthGrid = useMediaQuery(theme.breakpoints.up("md"));

  const isSuperAdmin = Boolean(
    user?.roles?.some((r) => r === "super_admin" || r?.name === "super_admin")
  );
  const farmTenantParam = useMemo(() => {
    if (!isSuperAdmin) return null;
    const raw = searchParams.get("tenant_id");
    if (raw == null || raw === "") return null;
    const id = Number(raw);
    return Number.isNaN(id) ? null : id;
  }, [isSuperAdmin, searchParams]);

  const planningSearchSuffix = searchParams.toString()
    ? `?${searchParams.toString()}`
    : "";

  const selectedActivityId = useMemo(() => {
    const raw = searchParams.get("activity_id");
    if (raw == null || raw === "") return null;
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  }, [searchParams]);

  const setActivityIdInUrl = useCallback(
    (id) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id == null) next.delete("activity_id");
          else next.set("activity_id", String(id));
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [focusDay, setFocusDay] = useState(() => new Date());
  const [allAnimals, setAllAnimals] = useState([]);
  const [activities, setActivities] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const deferredSearch = useDeferredValue(searchTerm);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [summaryVersion, setSummaryVersion] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createSubmitting, setCreateSubmitting] = useState(false);

  const parseDateSafe = (date) => {
    if (!date) return new Date();
    if (date instanceof Date) return new Date(date);
    if (typeof date === "string") return parseISO(date);
    return new Date();
  };

  const tenantReady = !isSuperAdmin || farmTenantParam != null;
  const needsActivity = tenantReady && selectedActivityId == null;

  useEffect(() => {
    const loadActivities = async () => {
      if (!tenantReady) {
        setActivities([]);
        return;
      }
      setActivitiesLoading(true);
      try {
        const rangeStart = format(startOfMonth(selectedDate), "yyyy-MM-dd");
        const rangeEnd = format(endOfMonth(selectedDate), "yyyy-MM-dd");
        const rows = await getActivities(
          { start_date: rangeStart, end_date: rangeEnd },
          { tenantId: farmTenantParam }
        );
        setActivities(Array.isArray(rows) ? rows : []);
      } catch (e) {
        console.error(e);
        setActivities([]);
      } finally {
        setActivitiesLoading(false);
      }
    };
    loadActivities();
  }, [selectedDate, tenantReady, farmTenantParam]);

  useEffect(() => {
    if (
      !tenantReady ||
      selectedActivityId == null ||
      activities.some((a) => a.id === selectedActivityId)
    ) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const act = await getActivityById(selectedActivityId, {
          tenantId: farmTenantParam,
          includeDeleted: true,
        });
        if (cancelled || !act) return;
        setActivities((prev) =>
          prev.some((p) => p.id === act.id) ? prev : [...prev, act]
        );
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantReady, selectedActivityId, activities, farmTenantParam]);

  useEffect(() => {
    const fetchAnimals = async () => {
      if (!tenantReady) {
        setAllAnimals([]);
        return;
      }
      try {
        const animalsData = await getAllAnimals({ tenantId: farmTenantParam });
        setAllAnimals(
          animalsData.filter((animal) => animal.is_active !== false)
        );
      } catch (error) {
        console.error("Error fetching animals:", error);
        setAllAnimals([]);
      }
    };
    fetchAnimals();
  }, [tenantReady, farmTenantParam]);

  useEffect(() => {
    const fetchAttendance = async () => {
      if (!tenantReady) {
        setAttendanceRecords([]);
        setLoading(false);
        return;
      }
      if (selectedActivityId == null) {
        setAttendanceRecords([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const attendanceData = await getMonthlyAttendance(
          selectedDate.getMonth() + 1,
          selectedDate.getFullYear(),
          {
            activityId: selectedActivityId,
            tenantId: farmTenantParam,
          }
        );
        setAttendanceRecords(
          Array.isArray(attendanceData) ? attendanceData : []
        );
      } catch (error) {
        console.error("Error fetching attendance:", error);
        setAttendanceRecords([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAttendance();
  }, [selectedDate, tenantReady, farmTenantParam, selectedActivityId]);

  useEffect(() => {
    const now = new Date();
    if (
      selectedDate.getMonth() === now.getMonth() &&
      selectedDate.getFullYear() === now.getFullYear()
    ) {
      const clamped = new Date(
        now.getFullYear(),
        now.getMonth(),
        Math.min(now.getDate(), getDaysInMonth(selectedDate))
      );
      setFocusDay(clamped);
    } else {
      setFocusDay(
        new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1)
      );
    }
  }, [selectedDate]);

  useEffect(() => {
    if (createOpen) {
      setCreateName(
        `Stock count — ${format(focusDay, "MMMM d, yyyy")}`
      );
    }
  }, [createOpen, focusDay]);

  const mergedAnimals = useMemo(() => {
    return allAnimals.map((animal) => {
      const animalRecords =
        attendanceRecords.find((item) => item.animal_id === animal.id)
          ?.records || [];

      return {
        ...animal,
        records: animalRecords.map((record) => ({
          ...record,
          date: parseDateSafe(record.date),
        })),
      };
    });
  }, [allAnimals, attendanceRecords]);

  const filteredAnimals = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();
    if (!q) return mergedAnimals;
    return mergedAnimals.filter((animal) => {
      const tag = (animal.tag_number || "").toLowerCase();
      const al = (animal.alias || "").toLowerCase();
      return tag.includes(q) || al.includes(q);
    });
  }, [mergedAnimals, deferredSearch]);

  const daysInMonth = useMemo(() => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    return eachDayOfInterval({
      start: new Date(year, month, 1),
      end: new Date(year, month, getDaysInMonth(selectedDate)),
    });
  }, [selectedDate]);

  const attendanceStats = useMemo(() => {
    const totalAnimals = filteredAnimals.length;
    const dailyStats = daysInMonth.map((day) => {
      const presentCount = filteredAnimals.filter((animal) =>
        animal.records.some(
          (r) => isSameDay(parseDateSafe(r.date), day) && r.status === "Present"
        )
      ).length;

      return {
        date: format(day, "MMM d"),
        present: presentCount,
        total: totalAnimals,
        percentage:
          totalAnimals > 0
            ? Math.round((presentCount / totalAnimals) * 100)
            : 0,
      };
    });

    return {
      totalAnimals,
      dailyStats,
      overallPercentage:
        totalAnimals > 0
          ? Math.round(
              (dailyStats.reduce((sum, day) => sum + day.present, 0) /
                (totalAnimals * daysInMonth.length)) *
                100
            )
          : 0,
    };
  }, [filteredAnimals, daysInMonth]);

  const selectedActivityOption = useMemo(() => {
    if (selectedActivityId == null) return null;
    return activities.find((a) => a.id === selectedActivityId) ?? null;
  }, [activities, selectedActivityId]);

  const handleStatusClick = (animalId, date, currentStatus) => {
    if (selectedActivityId == null) return;
    const animal = mergedAnimals.find((a) => a.id === animalId);
    const existing = animal?.records.find((r) =>
      isSameDay(parseDateSafe(r.date), date)
    );
    const resolved =
      existing?.status ||
      (currentStatus === "Present" || currentStatus === "Absent"
        ? currentStatus
        : "Unchecked");
    const statusNorm =
      resolved === "Present" || resolved === "Absent" || resolved === "Unchecked"
        ? resolved
        : "Unchecked";
    setCurrentRecord({
      animal_id: animalId,
      date: date.toISOString(),
      formattedDate: format(date, "MMMM d, yyyy"),
      status: statusNorm,
      notes: existing?.notes || "",
    });
    setOpenDialog(true);
  };

  const bumpSummary = () =>
    setSummaryVersion((v) => v + 1);

  const saveRecord = async () => {
    if (!currentRecord || selectedActivityId == null) return;

    setSaving(true);
    try {
      const recordToSave = {
        animal_id: currentRecord.animal_id,
        date: format(parseISO(currentRecord.date), "yyyy-MM-dd"),
        status: currentRecord.status,
        notes: currentRecord.notes,
        activity_id: selectedActivityId,
      };

      setAttendanceRecords((prev) => {
        const animalIndex = prev.findIndex(
          (a) => a.animal_id === currentRecord.animal_id
        );
        if (animalIndex === -1) {
          return [
            ...prev,
            {
              animal_id: currentRecord.animal_id,
              records: [
                {
                  id: undefined,
                  date: currentRecord.date,
                  status: currentRecord.status,
                  notes: currentRecord.notes,
                  activity_id: selectedActivityId,
                },
              ],
            },
          ];
        }

        return prev.map((animal) => {
          if (animal.animal_id !== currentRecord.animal_id) return animal;

          const recordIndex = animal.records.findIndex((r) =>
            isSameDay(parseISO(r.date), parseISO(currentRecord.date))
          );

          return {
            ...animal,
            records:
              recordIndex === -1
                ? [
                    ...animal.records,
                    {
                      date: currentRecord.date,
                      status: currentRecord.status,
                      notes: currentRecord.notes,
                      activity_id: selectedActivityId,
                    },
                  ]
                : animal.records.map((r, i) =>
                    i === recordIndex
                      ? {
                          ...r,
                          status: currentRecord.status,
                          notes: currentRecord.notes,
                          activity_id: selectedActivityId,
                        }
                      : r
                  ),
          };
        });
      });

      await saveAttendanceRecord(recordToSave, {
        tenantId: farmTenantParam,
      });
      setOpenDialog(false);
      bumpSummary();
    } catch (error) {
      console.error("Error saving record:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    if (selectedActivityId == null) return;
    setLoading(true);
    try {
      const payload = mergedAnimals.flatMap((animal) =>
        animal.records.map((record) => ({
          animal_id: animal.id,
          date: format(parseDateSafe(record.date), "yyyy-MM-dd"),
          status: record.status,
          notes: record.notes,
          activity_id: selectedActivityId,
        }))
      );
      if (payload.length === 0) {
        return;
      }
      await saveAllAttendance(payload, { tenantId: farmTenantParam });
      bumpSummary();
    } catch (error) {
      console.error("Error saving all records:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (selectedActivityId == null) return;
    try {
      const month = selectedDate.getMonth() + 1;
      const year = selectedDate.getFullYear();
      const blob = await exportAttendanceReport(month, year, {
        format: "csv",
        activityId: selectedActivityId,
        tenantId: farmTenantParam,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `animal_attendance_${year}_${String(month).padStart(2, "0")}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error exporting report:", error);
    }
  };

  const handleCreateActivity = async () => {
    if (!tenantReady) return;
    const name = createName.trim() || `Stock count — ${format(focusDay, "MMMM d, yyyy")}`;
    const day = format(focusDay, "yyyy-MM-dd");
    setCreateSubmitting(true);
    try {
      const created = await createActivity(
        {
          name,
          description: "",
          activity_type: "Livestock",
          start_date: day,
          end_date: day,
          is_all_day: true,
          priority: "Medium",
          status: "Planned",
          is_recurring: false,
        },
        { tenantId: farmTenantParam }
      );
      setActivities((prev) =>
        prev.some((p) => p.id === created.id) ? prev : [...prev, created]
      );
      setActivityIdInUrl(created.id);
      setCreateOpen(false);
      bumpSummary();
    } catch (e) {
      console.error(e);
    } finally {
      setCreateSubmitting(false);
    }
  };

  const gridDisabled = !tenantReady || selectedActivityId == null;

  return (
    <Box sx={{ pb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          px: { xs: 2, sm: 3 },
          py: 2,
          mb: 2,
          borderRadius: 0,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
          Stock taking
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose a planning activity for traceability, then record per-animal
          attendance for the month.
        </Typography>
        {isSuperAdmin && farmTenantParam == null && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Choose a farm the same way as on the{" "}
            <RouterLink to="/animals">animal list</RouterLink> (organization
            selector), then add{" "}
            <Box component="span" sx={{ fontFamily: "monospace" }} role="text">
              ?tenant_id=(farm id)
            </Box>{" "}
            to this address bar, or navigate from Animals after selecting the
            organization.
          </Alert>
        )}
        {tenantReady && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Autocomplete
              sx={{ flex: "1 1 280px", minWidth: 0, maxWidth: 480 }}
              options={activities}
              loading={activitiesLoading}
              value={selectedActivityOption}
              onChange={(_, v) => setActivityIdInUrl(v?.id ?? null)}
              getOptionLabel={(opt) =>
                opt ? `${opt.name} (#${opt.id})` : ""
              }
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Count activity"
                  placeholder="Select an activity overlapping this month"
                  helperText="Required. Shareable URL updates with ?activity_id="
                />
              )}
            />
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
              sx={{ mt: 0.5 }}
            >
              New activity
            </Button>
            <Button
              component={RouterLink}
              to={`/planning/activities${planningSearchSuffix}`}
              variant="text"
              sx={{ mt: 0.5 }}
            >
              All activities
            </Button>
          </Box>
        )}
        {needsActivity && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Select or create a count activity before recording stock taking.
          </Alert>
        )}
        <AttendanceFilters
          selectedDate={selectedDate}
          onDateChange={(d) => d && setSelectedDate(d)}
          searchTerm={searchTerm}
          onSearchChange={(e) => setSearchTerm(e.target.value)}
          resultCount={filteredAnimals.length}
          totalCount={mergedAnimals.length}
          onSaveAll={handleSaveAll}
          onPrint={() => window.print()}
          onExport={handleExport}
          actionsDisabled={gridDisabled}
        />
      </Paper>

      <Box sx={{ px: { xs: 2, sm: 3 } }}>
        {!gridDisabled && (
          <StockTakingActivitySummary
            activityId={selectedActivityId}
            tenantId={farmTenantParam}
            dataVersion={summaryVersion}
          />
        )}
        {loading ? (
          <LinearProgress />
        ) : (
          <>
            {gridDisabled ? (
              <Alert severity="info">
                Stock-taking grid is available after you pick an activity.
              </Alert>
            ) : (
              <>
                {!showMonthGrid && (
                  <AttendanceDayList
                    animals={filteredAnimals}
                    focusDay={focusDay}
                    monthAnchor={selectedDate}
                    onFocusDayChange={setFocusDay}
                    onStatusClick={handleStatusClick}
                    onAnimalClick={(animalId) => navigate(`/animal/${animalId}`)}
                  />
                )}

                {showMonthGrid && (
                  <AttendanceTable
                    animals={filteredAnimals}
                    daysInMonth={daysInMonth}
                    selectedDate={selectedDate}
                    onStatusClick={handleStatusClick}
                    onAnimalClick={(animalId) => navigate(`/animal/${animalId}`)}
                  />
                )}

                <AttendanceStats
                  dailyStats={attendanceStats.dailyStats}
                  overallPercentage={attendanceStats.overallPercentage}
                />
              </>
            )}

            <AttendanceDialog
              open={openDialog}
              onClose={() => setOpenDialog(false)}
              record={currentRecord}
              onRecordChange={setCurrentRecord}
              onSave={saveRecord}
              saving={saving}
            />
          </>
        )}
      </Box>

      <Dialog
        open={createOpen}
        onClose={() => !createSubmitting && setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create count activity</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Creates a single-day Livestock activity for{" "}
            <strong>{format(focusDay, "MMMM d, yyyy")}</strong> (the day
            selected in the mobile day view, or today when this month is
            current). You can edit details later under All activities.
          </Typography>
          <TextField
            label="Activity name"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={createSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateActivity}
            disabled={createSubmitting}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
